import * as XLSX from 'xlsx';

export interface ParsedLearner {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  isSelected: boolean;
  isDuplicate: boolean;
  duplicateGroup?: string;
  hasError: boolean;
  errorMessage?: string;
  rowNumber: number;
  isEmailDuplicate: boolean;
}

export interface ParsedFileResult {
  learners: ParsedLearner[];
  errors: string[];
  unmappedColumns: string[];
}

// Column name mappings for smart detection
const FIRST_NAME_VARIANTS = [
  'firstname',
  'first_name',
  'first name',
  'fname',
  'given name',
  'givenname',
  'імʼя',
  "ім'я",
];

const LAST_NAME_VARIANTS = [
  'lastname',
  'last_name',
  'last name',
  'lname',
  'surname',
  'family name',
  'familyname',
  'прізвище',
];

const EMAIL_VARIANTS = [
  'email',
  'e-mail',
  'email address',
  'emailaddress',
  'mail',
  'електронна пошта',
  'пошта',
];

const FULL_NAME_VARIANTS = [
  'full name',
  'fullname',
  'full_name',
  'name',
  "повне ім'я",
];

function normalizeColumnName(name: string): string {
  return name.toLowerCase().trim();
}

function findColumnIndex(headers: string[], variants: string[]): number {
  for (let i = 0; i < headers.length; i++) {
    const normalized = normalizeColumnName(headers[i]);
    if (variants.includes(normalized)) {
      return i;
    }
  }
  return -1;
}

function splitFullName(fullName: string): {
  firstName: string;
  lastName: string;
} {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) {
    return {firstName: parts[0], lastName: ''};
  }
  const firstName = parts[0];
  const lastName = parts.slice(1).join(' ');
  return {firstName, lastName};
}

function generateId(): string {
  return Math.random().toString(36).substring(2, 11);
}

function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

function detectDuplicates(learners: ParsedLearner[]): ParsedLearner[] {
  const nameGroups: Record<string, ParsedLearner[]> = {};
  const emailGroups: Record<string, ParsedLearner[]> = {};

  // Group by full name (case-insensitive)
  learners.forEach(learner => {
    const fullName = `${learner.firstName} ${learner.lastName}`
      .toLowerCase()
      .trim();
    if (!nameGroups[fullName]) {
      nameGroups[fullName] = [];
    }
    nameGroups[fullName].push(learner);

    // Group by email (case-insensitive)
    const email = learner.email.toLowerCase().trim();
    if (email) {
      if (!emailGroups[email]) {
        emailGroups[email] = [];
      }
      emailGroups[email].push(learner);
    }
  });

  // Mark duplicates (same name)
  Object.keys(nameGroups).forEach(name => {
    const group = nameGroups[name];
    if (group.length > 1) {
      // Same name - mark as duplicates
      const groupId = generateId();
      group.forEach(learner => {
        learner.isDuplicate = true;
        learner.duplicateGroup = groupId;
      });
    }
  });

  // Mark duplicate emails - keep only first occurrence selectable
  Object.keys(emailGroups).forEach(email => {
    const group = emailGroups[email];
    if (group.length > 1) {
      // Skip first occurrence, mark rest as email duplicates that can't be selected
      group.slice(1).forEach(learner => {
        learner.isEmailDuplicate = true;
        learner.isSelected = false;
      });
    }
  });

  return learners;
}

export function parseCSV(content: string): string[][] {
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentCell = '';
  let inQuotes = false;

  for (let i = 0; i < content.length; i++) {
    const char = content[i];
    const nextChar = content[i + 1];

    if (inQuotes) {
      if (char === '"' && nextChar === '"') {
        currentCell += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        currentCell += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        currentRow.push(currentCell.trim());
        currentCell = '';
      } else if (char === '\n' || (char === '\r' && nextChar === '\n')) {
        currentRow.push(currentCell.trim());
        if (currentRow.some(cell => cell !== '')) {
          rows.push(currentRow);
        }
        currentRow = [];
        currentCell = '';
        if (char === '\r') i++;
      } else if (char !== '\r') {
        currentCell += char;
      }
    }
  }

  // Add last row if not empty
  currentRow.push(currentCell.trim());
  if (currentRow.some(cell => cell !== '')) {
    rows.push(currentRow);
  }

  return rows;
}

export async function parseFile(file: File): Promise<ParsedFileResult> {
  const errors: string[] = [];
  const unmappedColumns: string[] = [];
  let rows: string[][] = [];

  try {
    if (file.name.endsWith('.csv')) {
      const text = await file.text();
      rows = parseCSV(text);
    } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, {type: 'array'});
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json<string[]>(firstSheet, {
        header: 1,
      });
      rows = jsonData.map(row => row.map(cell => String(cell ?? '')));
    } else {
      errors.push('Unsupported file format');
      return {learners: [], errors, unmappedColumns};
    }
  } catch (e) {
    errors.push('Failed to parse file');
    return {learners: [], errors, unmappedColumns};
  }

  if (rows.length < 2) {
    errors.push('File must contain at least a header row and one data row');
    return {learners: [], errors, unmappedColumns};
  }

  const headers = rows[0];
  const dataRows = rows.slice(1);

  // Find column indices
  const firstNameIndex = findColumnIndex(headers, FIRST_NAME_VARIANTS);
  const lastNameIndex = findColumnIndex(headers, LAST_NAME_VARIANTS);
  const emailIndex = findColumnIndex(headers, EMAIL_VARIANTS);
  const fullNameIndex = findColumnIndex(headers, FULL_NAME_VARIANTS);

  // If no email column, that's a critical error
  if (emailIndex === -1) {
    errors.push(
      'Could not find email column. Please include a column named "Email"',
    );
    return {learners: [], errors, unmappedColumns};
  }

  // Use full name if:
  // 1. No separate FirstName column found, OR
  // 2. FirstName found but no LastName (and we have a full name column that can be split)
  const useFullName =
    fullNameIndex !== -1 &&
    (firstNameIndex === -1 || (firstNameIndex !== -1 && lastNameIndex === -1));

  // Track unmapped columns
  headers.forEach((header, index) => {
    if (
      index !== firstNameIndex &&
      index !== lastNameIndex &&
      index !== emailIndex &&
      index !== fullNameIndex &&
      header.trim() !== ''
    ) {
      unmappedColumns.push(header);
    }
  });

  // Parse data rows
  const learners: ParsedLearner[] = [];

  dataRows.forEach((row, rowIndex) => {
    const rowNum = rowIndex + 2; // +2 because of header row and 0-index
    const email = row[emailIndex]?.trim() || '';

    let firstName = '';
    let lastName = '';

    if (useFullName) {
      const fullName = row[fullNameIndex]?.trim() || '';
      const split = splitFullName(fullName);
      firstName = split.firstName;
      lastName = split.lastName;
    } else {
      firstName = row[firstNameIndex]?.trim() || '';
      lastName = lastNameIndex !== -1 ? row[lastNameIndex]?.trim() || '' : '';

      // If firstName column contains full name (has space), try to split
      if (lastNameIndex === -1 && firstName.includes(' ')) {
        const split = splitFullName(firstName);
        firstName = split.firstName;
        lastName = split.lastName;
      }
    }

    // Skip completely empty rows
    if (!email && !firstName && !lastName) {
      return;
    }

    // Determine error state
    let hasError = false;
    let errorMessage: string | undefined;

    if (!email) {
      hasError = true;
      errorMessage = 'Missing email';
    } else if (!isValidEmail(email)) {
      hasError = true;
      errorMessage = `Invalid email: "${email}"`;
    } else if (!firstName && !lastName) {
      hasError = true;
      errorMessage = 'Missing name';
    }

    learners.push({
      id: generateId(),
      firstName,
      lastName,
      email,
      isSelected: !hasError, // Don't select rows with errors by default
      isDuplicate: false,
      hasError,
      errorMessage,
      rowNumber: rowNum,
      isEmailDuplicate: false,
    });
  });

  // Detect duplicates
  const learnersWithDuplicates = detectDuplicates(learners);

  return {
    learners: learnersWithDuplicates,
    errors,
    unmappedColumns,
  };
}
